<template>
  <b-overlay
    variant="white"
    spinner-variant="primary"
    blur="0"
    opacity=".75"
    rounded="sm"
    :show="isLoading"
  >
    <div class="auth-wrapper auth-v2">
      <b-row class="auth-inner m-0">
        <b-link class="brand-logo" style="display: none;">
          <Logo />
          <h2 class="brand-text text-primary ml-1 school-title">
            {{ appName }}
          </h2>
        </b-link>
        <b-col
          cols="12"
          lg="6"
          class="d-flex align-items-center p-1 auth-bg justify-content-center"
        >
          <div class="w-100 d-flex align-items-center justify-content-center px-1 flex-column">
            <b-img
              fluid
              :src="imgUrl"
              alt="Đăng nhập"
            />
            <b-card-title
              title-tag="h2"
              class="font-weight-bold mb-1 mt-2 text-center school-title"
            >
              <span class="school-name-part">TRƯỜNG CHÍNH TRỊ</span>
              <span class="school-name-part">TỈNH HẬU GIANG</span>
            </b-card-title>
          </div>
        </b-col>
        <b-col
          cols="12"
          lg="6"
          class="d-flex align-items-center auth-bg px-2 p-lg-1"
        >
          <b-col
            cols="12"
            sm="8"
            md="8"
            lg="6"
            class="px-xl-2 mx-auto form-container"
          >
            <validation-observer
              ref="loginFormRef"
              #default="{invalid}"
            >
              <b-form class="auth-login-form mt-2">
                <b-card-title
                  title-tag="h2"
                  class="font-weight-bolder mb-3 text-center text-dark"
                >
                  ĐĂNG NHẬP
                </b-card-title>

                <b-form-group
                  label="Tài khoản"
                  label-for="username"
                  class="form-group-mobile"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Tài khoản"
                    vid="username"
                    rules="required"
                  >
                    <b-form-input
                      id="username"
                      v-model="username"
                      name="username"
                      tabindex="1"
                      :state="getElementState(errors)"
                      class="form-input-mobile"
                      @keyup.enter="onLogin"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-form-group class="form-group-mobile">
                  <div class="d-flex justify-content-between">
                    <label for="password">Mật khẩu</label>
                    <b-link :to="{ name: 'forgotPassword' }" class="forgot-password-link">
                      <small>Quên mật khẩu?</small>
                    </b-link>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="Mật khẩu"
                    vid="password"
                    rules="required"
                  >
                    <b-input-group
                      class="input-group-merge form-input-mobile"
                      :class="errors.length > 0 ? 'is-invalid' : null"
                    >
                      <b-form-input
                        id="password"
                        v-model="password"
                        class="form-control-merge"
                        name="password"
                        tabindex="2"
                        :type="passwordFieldType"
                        :state="getElementState(errors)"
                        @keyup.enter="onLogin"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <b-button
                  variant="danger"
                  block
                  :disabled="invalid"
                  class="btn-mobile"
                  @click="onLogin"
                >
                  Đăng nhập
                </b-button>
              </b-form>
            </validation-observer>
          </b-col>
        </b-col>
      </b-row>
    </div>
  </b-overlay>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BCard,
  BLink,
  BCardText,
  BInputGroup,
  BInputGroupAppend,
  BOverlay,
  BRow,
  BCol,
  BCardTitle,
  BImg,
} from 'bootstrap-vue'
import Logo from '@core/layouts/components/Logo.vue'
import { required } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { mapActions } from 'vuex'
import useJwt from '@/auth/jwt/useJwt'
import { LOGIN_TYPE } from '@/const/type'
import store from '@/store'

export default {
  name: 'Login',
  components: {
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BCard,
    BLink,
    Logo,
    BCardText,
    BInputGroup,
    BInputGroupAppend,
    BOverlay,
    ValidationProvider,
    ValidationObserver,
    BRow,
    BCol,
    BCardTitle,
    BImg,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      username: '',
      password: '',
      status: '',
      isLoading: false,
      required,
      appName: process.env.VUE_APP_NAME,
      sideImg: require('@/assets/images/pages/hgi-logo-truong-chinh-tri.png'),
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/login-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  methods: {
    ...mapActions({
      login: 'auth/login',
      verify: 'auth/verify',
      updatePortalProgramme: 'layout/updatePortalProgramme',
    }),
    getElementState(errors) {
      return errors.length > 0 ? false : null
    },
    async onLogin() {
      const valid = await this.$refs
        .loginFormRef
        .validate()
      if (valid) {
        this.isLoading = true
        try {
          await this.$recaptchaLoaded()
          const recaptchaToken = await this.$recaptcha('login')
          await this.login({
            username: this.username,
            password: this.password,
            loginType: +LOGIN_TYPE.NORMAL,
            recaptchaToken,
          })
          const user = await this.verify()
          if (user) {
            // this.$socket.emit('register_server_socket', user.studentId)
            this.$ability.update(user.abilities)
            if (user.programmes) {
              if (user.programmes.length > 0 && !user.portalProgrammeId) {
                await this.updatePortalProgramme({
                  studentId: user.studentId,
                  programmeId: user.programmes[0].id,
                })
              }
            } else {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Đăng nhập thất bại, vui lòng gán chương trình đào tạo cho sinh viên!',
                  icon: 'XCircleIcon',
                  variant: 'danger',
                },
              })
              return
            }
            try {
              await this.$router.replace({ name: 'portalDashboard' })
            } catch (e) {
              // empty
            }
          } else {
            useJwt.handleLogout()
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Không lấy được thông tin người dùng',
                icon: 'XCircleIcon',
                variant: 'danger',
              },
            })
          }
        } catch (e) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `[${e.code}] ${e.message}`,
              icon: 'XCircleIcon',
              variant: 'danger',
            },
          })
        } finally {
          this.isLoading = false
        }
      }
    },
    onLoginSso() {
      const { protocol, hostname } = window.location
      const redirectUri = `${protocol}//${hostname}/auth/login/ssoCallback&scope=openid+profile&nonce=asd`
      window.location.href = `${process.env.VUE_APP_SSO_URL}/oauth2/authorize?response_type=code+id_token+token&client_id=${process.env.VUE_APP_SSO_CLIENT_ID}&redirect_uri=${redirectUri}`
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';

.auth-wrapper.auth-v2 {
  background-image: url('../../assets/images/pages/hgi-bg-truong-chinh-tri.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(220, 53, 69, 0.4); 
    z-index: 1;
  }
}

.auth-inner {
  position: relative;
  z-index: 2;
}

.auth-bg {
  background-color: rgba(255, 255, 255, 0.8) !important;
}

.form-container {
  min-width: 70%;
}

@media (max-width: 992px) {
  .form-container {
    width: 100%;
    min-width: 70%;
    padding: 0 1rem;
  }

  .form-group-mobile {
    width: 100%;
    padding: 0;
    min-width: 100%;
  }

  .form-input-mobile {
    width: 100%;
    min-width: 100%;
  }

  .btn-mobile {
    width: 100% !important;
    margin: 0;
    min-width: 100%;
  }

  .auth-inner {
    .row {
      gap: 0 !important;
    }
  }

  .auth-bg {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;

    &:first-of-type {
      align-items: flex-end !important;
      padding-bottom: 0 !important;
    }

    &:last-of-type {
      align-items: flex-start !important;
      padding-top: 0 !important;
    }
  }

  .auth-login-form {
    margin: 0 1rem;
    width: calc(100% - 2rem);
  }
}

.auth-login-form {
  background: rgba(255, 255, 255, 0.9);
  padding: 2rem;
  border-radius: 0.5rem;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  min-width: 100%;
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.school-title {
  color: #FFD54F !important; 
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  font-weight: 900 !important;

  .school-name-part {
    display: inline;
    
    &:first-child::after {
      content: " ";
    }
  }
}

@media (max-width: 576px) {
  .school-title {
    .school-name-part {
      display: block;
      line-height: 1.2;

      &:first-child::after {
        content: "";
      }

      &:last-child {
        margin-top: 0.5rem;
      }
    }
  }
}

.forgot-password-link {
  color: darken(#FFD54F, 10%) !important;
  text-decoration: none;
  text-shadow: .5px .5px 1px rgba(0, 0, 0, 0.2);
  font-weight: 900 !important;
  
  &:hover {
    text-decoration: underline;
  }
}

.btn-danger {
  background-color: #E81F27 !important; 
  border-color: #E81F27 !important;
  cursor: pointer;
  position: relative;
  z-index: 3;
  opacity: 0.95 !important;

  &:hover {
    background-color: #D41920 !important; 
    border-color: #D41920 !important;
    opacity: 1 !important;
  }
}

::v-deep .b-overlay {
  .b-overlay-wrap {
    > [aria-busy="true"] {
      z-index: 10;
    }
  }
}
</style>
